const DOWNLOADABLE_FILE_PATTERN =
	/\.(?:pdf|zip|rar|7z|docx?|xlsx?|pptx?|jpe?g|png|webp|gif|svg|mp4|mp3)(?:[?#].*)?$/i;
const BLOCKED_CLIENT_PATH_PATTERN =
	/\/(?:wp-admin|wp-login\.php|wp-json|xmlrpc\.php|wp-content|wp-includes)(?:\/|$)/i;
const BLOCKED_CLIENT_FRONTEND_PATH_PATTERN =
	/\/(?:cart|checkout|my-account)(?:\/|$)/i;
const BLOCKED_CLIENT_PARAMS = [
	'_wpnonce',
	'add-to-cart',
	'customize_changeset_uuid',
	'preview',
	'replytocom',
	'wc-ajax',
];
const SKIP_NAVIGATION_SELECTOR = [
	'[download]',
	'[data-bs-toggle]',
	'[data-bs-target]',
	'[data-toggle]',
	'[data-target]',
	'[aria-controls]',
	'.scroll-to',
	'.open-modal-btn-3',
	'.open-gift-button',
	'[data-no-spa]',
	'.no-spa',
	'.ab-item',
].join(',');

const isPlainLeftClick = (event) =>
	event &&
	!event.defaultPrevented &&
	0 === event.button &&
	!event.metaKey &&
	!event.ctrlKey &&
	!event.shiftKey &&
	!event.altKey;

const isDownloadableFile = (url) =>
	DOWNLOADABLE_FILE_PATTERN.test(url.pathname);

const isSamePageHash = (url) => {
	const currentUrl = new URL(window.location.href);

	return (
		Boolean(url.hash) &&
		url.origin === currentUrl.origin &&
		url.pathname === currentUrl.pathname &&
		url.search === currentUrl.search
	);
};

const isHashOnlyHref = (href) => href.startsWith('#');

const hasBlockedClientParam = (url) =>
	BLOCKED_CLIENT_PARAMS.some((param) => url.searchParams.has(param));

const canRouteClientSide = (url) =>
	url.origin === window.location.origin &&
	!isDownloadableFile(url) &&
	!BLOCKED_CLIENT_PATH_PATTERN.test(url.pathname) &&
	!BLOCKED_CLIENT_FRONTEND_PATH_PATTERN.test(url.pathname) &&
	!hasBlockedClientParam(url);

export const getNavigationDecision = (
	link,
	event,
	isServerRouteOnly = false
) => {
	let shouldRouteClientSide = false;
	if (
		!isPlainLeftClick(event) ||
		!link ||
		!document.documentElement.contains(link)
	) {
		return {
			shouldShowLoader: false,
			shouldRouteClientSide,
			isHashNavigation: false,
			url: null,
		};
	}

	const rawHref = (link.getAttribute('href') || '').trim();
	const target = (link.getAttribute('target') || '').trim().toLowerCase();

	if (
		!rawHref ||
		'#' === rawHref ||
		(target && '_self' !== target) ||
		link.matches(SKIP_NAVIGATION_SELECTOR)
	) {
		return {
			shouldShowLoader: false,
			shouldRouteClientSide,
			isHashNavigation: false,
			url: null,
		};
	}

	if (isHashOnlyHref(rawHref)) {
		return {
			shouldShowLoader: false,
			shouldRouteClientSide,
			isHashNavigation: true,
			url: new URL(link.href, window.location.href),
		};
	}

	let url;

	try {
		url = new URL(link.href, window.location.href);
	} catch (error) {
		return {
			shouldShowLoader: false,
			shouldRouteClientSide: false,
			isHashNavigation: false,
			url: null,
		};
	}

	if (!['http:', 'https:'].includes(url.protocol) || isSamePageHash(url)) {
		return {
			shouldShowLoader: false,
			shouldRouteClientSide,
			isHashNavigation: isSamePageHash(url),
			url,
		};
	}
	if (!isServerRouteOnly) {
		shouldRouteClientSide = canRouteClientSide(url);
	}

	return {
		shouldShowLoader: true,
		shouldRouteClientSide,
		isHashNavigation: false,
		url,
	};
};

class ThemeAnchorLoaderManager {
	constructor(options = {}) {
		const normalizedOptions = options;

		this.loaderSelector = 'body > #spinner-loader';
		this.isServerRouteOnly = Boolean(normalizedOptions?.isServerRouteOnly);
		this.timer = null;
		this.rafId = null;
		this.initialized = false;
		this.clientNavigationHandler = null;

		this.boundHandleClick = this.handleClick.bind(this);
		this.boundHideLoader = this.hideLoader.bind(this);
		this.boundHandleVisibilityChange =
			this.handleVisibilityChange.bind(this);
	}

	init() {
		if (this.initialized) {
			return;
		}
		this.initialized = true;
		document.addEventListener('click', this.boundHandleClick);
		window.addEventListener('pageshow', this.boundHideLoader);
		window.addEventListener('pagehide', this.boundHideLoader);
		window.addEventListener('popstate', this.boundHideLoader);
		window.addEventListener('hashchange', this.boundHideLoader);
		document.addEventListener(
			'visibilitychange',
			this.boundHandleVisibilityChange
		);
		this.hideLoader();
	}

	handleVisibilityChange() {
		if ('hidden' === document.visibilityState) {
			this.hideLoader();
		}
	}

	handleClick(event) {
		const link = event.target?.closest?.('a[href]');
		const decision = getNavigationDecision(
			link,
			event,
			this.isServerRouteOnly
		);

		if (!decision.shouldShowLoader) {
			return;
		}

		if (
			!this.isServerRouteOnly &&
			decision.shouldRouteClientSide &&
			'function' === typeof this.clientNavigationHandler
		) {
			this.clearTimer();
			this.clientNavigationHandler(decision, event);
			return;
		}

		this.clearTimer();
		// Chỉ hiện khi chuyển trang chậm hơn 200ms để tránh nhấp nháy.
		this.timer = window.setTimeout(() => {
			this.showLoader();
		}, 200);
	}

	setClientNavigationHandler(handler) {
		this.clientNavigationHandler =
			'function' === typeof handler ? handler : null;
	}

	showLoader() {
		const loader = this.getLoader();

		if (this.rafId) {
			window.cancelAnimationFrame(this.rafId);
		}

		this.rafId = window.requestAnimationFrame(() => {
			this.rafId = null;
			loader.classList.add('is-active');
			loader.setAttribute('aria-hidden', 'false');
		});
	}

	hideLoader() {
		this.clearTimer();

		if (this.rafId) {
			window.cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}

		const loader = document.querySelector(this.loaderSelector);

		if (!loader) {
			return;
		}

		loader.classList.remove('is-active');
		loader.style.display = '';
		loader.setAttribute('aria-hidden', 'true');
	}

	clearTimer() {
		if (!this.timer) {
			return;
		}

		window.clearTimeout(this.timer);
		this.timer = null;
	}

	getLoader() {
		let loader = document.querySelector(this.loaderSelector);

		if (loader) {
			return loader;
		}

		loader = document.createElement('div');
		loader.id = 'spinner-loader';
		loader.setAttribute('aria-hidden', 'true');
		loader.innerHTML = `
			<div class="loader-overlay">
				<div class="loader-spinner" role="status" aria-live="polite">
					<div class="spinner"></div>
					<p>Đang tải...</p>
				</div>
			</div>
		`;
		document.body.appendChild(loader);

		return loader;
	}
}

const LOADER_MANAGER_KEY = 'JWThemeAnchorLoaderManager';

const getLoaderOnNavigation = (options = {}) => {
	window[LOADER_MANAGER_KEY] ??= new ThemeAnchorLoaderManager(options);

	const { isServerRouteOnly } = options;

	window[LOADER_MANAGER_KEY].isServerRouteOnly = Boolean(
		isServerRouteOnly
	);

	return window[LOADER_MANAGER_KEY];
};

export const initNavigationLoader = (isServerRouteOnly) => {
	getLoaderOnNavigation({
		isServerRouteOnly,
	}).init();
};

export const ensureNavigationLoader = () => {
	getLoaderOnNavigation().getLoader();
};

export const showNavigationLoader = () => {
	getLoaderOnNavigation().showLoader();
};

export const hideNavigationLoader = () => {
	getLoaderOnNavigation().hideLoader();
};

export const setClientNavigationHandler = (handler) => {
	getLoaderOnNavigation().setClientNavigationHandler(handler);
};
